import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StrapiDataService } from '../strapi/strapi-data.service';
import { QueryParams } from '@zerops/zef/entities';
import {
  GitHubRecipe,
  GitHubRecipeApiResponse,
  GitHubRecipeEnvironment,
  GitHubRecipeRepository,
  GitHubRecipeYaml,
  RelatedRecipesResponse,
  getDefaultEnvironment
} from '@zerops/models/recipes';
import { StaticContent } from './recipe-base.model';
import { parseEnvironmentName } from './recipe-base.state';

@Injectable({
  providedIn: 'root'
})
export class RecipeBaseApi {
  // # Deps
  #strapiService = inject(StrapiDataService);
  #http = inject(HttpClient);

  list$(queryParams?: QueryParams | string) {
    return this.#strapiService.recipes$(queryParams);
  }

  categories$(queryParams?: QueryParams | string) {
    return this.#strapiService.categories$(queryParams);
  }

  languagesFrameworks$(queryParams?: QueryParams | string) {
    return this.#strapiService.languagesFrameworks$(queryParams);
  }

  loadGitHubRecipe(url: string, specificYaml?: string): Observable<GitHubRecipe> {
    // Branch travels inside the url as `repo@branch`; the API falls back to
    // main/master on its own when none is given.
    let apiUrl = `/api/recipe/info?url=${encodeURIComponent(url)}`;

    if (specificYaml) {
      apiUrl += `&yaml=${encodeURIComponent(specificYaml)}`;
    }
    
    return this.#http.get<GitHubRecipeApiResponse>(apiUrl).pipe(
      map(response => this.#transformGitHubResponse(response, url, specificYaml))
    );
  }

  #transformGitHubResponse(
    response: GitHubRecipeApiResponse,
    url: string,
    specificYaml?: string
  ): GitHubRecipe {
    const repository = this.#parseRepositoryFromUrl(url);

    // FAIL LOUD: a null/non-object/error response must NOT be cached as a
    // successful empty recipe (that swallows the failure and blocks retry).
    // Throw so the effect's catchError path handles it instead. A legit empty
    // result (`environments: []`) is only preserved when the normal envelope
    // (name/content present) exists.
    if (!response || typeof response !== 'object') {
      throw new Error('GitHub recipe response is null or not an object');
    }

    if (response.errors != null) {
      throw new Error('GitHub recipe response contains errors');
    }

    const hasEnvelope = response.name != null || response.content != null
      || Array.isArray(response.environments);
    if (!hasEnvelope) {
      throw new Error('GitHub recipe response is missing the name/environments envelope');
    }

    // DEFENSIVE: legacy flat-root repos return `environments: []`, a valid
    // empty result. Guard every access so a sparse-but-valid envelope yields
    // an empty-but-valid GitHubRecipe instead of throwing.
    const rawEnvironments = response.environments ?? [];

    // Parse each environment folder into rich per-env detail.
    const parsedEnvironments: GitHubRecipeEnvironment[] = rawEnvironments
      .filter((env): env is NonNullable<typeof env> => !!env)
      .map((env) => {
        const { order, key, name } = parseEnvironmentName(env.name ?? '');
        return {
          key,
          order,
          name,
          displayName: env.name ?? '',
          content: env.content ?? '',
          extracts: env.extracts,
          import: env.import ?? '',
          projectName: env.projectName ?? '',
          services: env.services ?? []
        };
      })
      .sort((a, b) => a.order - b.order);

    // Back-compat YAML list: one import YAML per environment. `name`/`environment`
    // carry the slug so URL-param matching (which uses env keys) still resolves.
    // `displayName` carries the CLEAN parsed name (no order prefix) so the
    // recipe-detail env selector matches the native Strapi path.
    const yamls: GitHubRecipeYaml[] = parsedEnvironments.map((env) => ({
      name: env.key,
      content: env.import,
      url: '',
      environment: env.key,
      isDefault: false,
      displayName: env.name,
      type: 'import' as const
    }));

    // Resolve default via the shared preferred-env logic (production-first),
    // then mark it on the corresponding YAML.
    const defaultEnv = getDefaultEnvironment(parsedEnvironments);
    let defaultYaml = (defaultEnv
      ? yamls.find((y) => y.environment === defaultEnv.key)
      : undefined) ?? yamls[0];

    // Respect an explicit yaml request (specificYaml is always an env key/slug).
    if (specificYaml) {
      const specificMatch = yamls.find((y) =>
        y.name === specificYaml ||
        y.environment === specificYaml
      );
      if (specificMatch) {
        defaultYaml = specificMatch;
      }
    }

    if (defaultYaml) {
      defaultYaml.isDefault = true;
    }

    const environments = parsedEnvironments.map((env) => env.key);

    return {
      id: btoa(url), // Base64 encode URL as ID
      sourceUrl: url,
      repository,
      readmeMd: response?.content, // recipe-root README, raw markdown
      yamls,
      defaultYaml,
      environments,
      selectedYaml: specificYaml,
      // -- additive rich data for the read-path
      name: response?.name,
      extracts: response?.extracts,
      parsedEnvironments
    };
  }

  #parseRepositoryFromUrl(url: string): GitHubRecipeRepository {
    const match = url.match(/github\.com\/([^/@]+)\/([^/@]+?)(?:\.git)?(?:@([^/]+))?(?:\/|$)/);

    if (match) {
      const [ , owner, repo, branch ] = match;
      return {
        owner,
        repo,
        // Clone target without the `@branch` suffix; the branch rides in `-b`.
        url: url.replace(/@[^/]+\/?$/, ''),
        branch: branch || undefined
      };
    }

    return {
      owner: 'unknown',
      repo: 'unknown',
      url
    };
  }

  /**
   * Fetch related recipes for a given recipe ID/slug
   * @param recipeId - Recipe ID or slug
   * @param limit - Maximum number of results (default: 4, max: 10)
   */
  relatedRecipes$(recipeId: string | number, limit = 4): Observable<RelatedRecipesResponse> {
    return this.#strapiService.relatedRecipes$(recipeId, limit);
  }

  /**
   * Fetch static content (environment guides, service type docs) from CMS
   */
  staticContent$(): Observable<StaticContent> {
    return this.#strapiService.staticContent$();
  }
}
